import React from "react";
import { Waves, Plane, MapPin, Castle, ArrowUpRight, ArrowRight } from "lucide-react";
import { useLang } from "@/context/LanguageContext";

const ICONS = {
  zoomarine: Waves,
  slidesplash: Waves,
  lagos: MapPin,
  albufeira: MapPin,
  faro: Plane,
  lisboa: Plane,
  sevilha: Plane,
  porto: Plane,
  beja: Plane,
};

const IMAGES = {
  zoomarine:
    "https://images.unsplash.com/photo-1583429553590-1668c2ff1f30?auto=format&fit=crop&w=1100&q=72",
  slidesplash:
    "https://images.unsplash.com/photo-1502920917128-1aa500764cbd?auto=format&fit=crop&w=1100&q=72",
  lagos:
    "https://images.unsplash.com/photo-1652092521226-3e03a716ae86?crop=entropy&cs=srgb&fm=jpg&w=1100&q=72",
  albufeira:
    "https://images.unsplash.com/photo-1591801074660-15ea1eaaf3a4?auto=format&fit=crop&w=1100&q=72",
  faro:
    "https://images.unsplash.com/photo-1542296332-2e4473faf563?auto=format&fit=crop&w=1100&q=72",
  lisboa:
    "https://images.unsplash.com/photo-1513735492246-483525079686?auto=format&fit=crop&w=1100&q=72",
  sevilha:
    "https://images.unsplash.com/photo-1559339352-11d035aa65de?auto=format&fit=crop&w=1100&q=72",
  porto:
    "https://images.unsplash.com/photo-1555881400-74d7acaacd8b?auto=format&fit=crop&w=1100&q=72",
  beja:
    "https://images.unsplash.com/photo-1542296332-2e4473faf563?auto=format&fit=crop&w=1100&q=72",
};

const AIRPORT_IDS = ["faro", "lisboa", "sevilha", "porto", "beja"];

const triggerBooking = (destination) => {
  window.dispatchEvent(
    new CustomEvent("faustitur:prefill-booking", { detail: { destination } })
  );
  const target = document.getElementById("booking");
  if (target) target.scrollIntoView({ behavior: "smooth", block: "start" });
};

const AirportCard = ({ item, t }) => {
  const Icon = ICONS[item.id] || Plane;
  const onClick = (e) => {
    e.preventDefault();
    triggerBooking(item.name);
  };
  return (
    <a
      href="#booking"
      onClick={onClick}
      data-testid={`service-card-${item.id}`}
      className="airport-card group block relative overflow-hidden"
    >
      <div className="relative h-44 sm:h-48 overflow-hidden">
        <img
          src={IMAGES[item.id]}
          alt={item.name}
          className="bento-image w-full h-full object-cover"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/55 to-black/10" />
        <div className="absolute top-3 left-3 flex items-center gap-1.5 px-2 py-1 bg-[color:var(--gold)] text-black">
          <Icon className="h-3 w-3" />
          <span className="text-[9px] tracking-[0.25em] uppercase font-semibold">Airport</span>
        </div>
        <div className="absolute bottom-0 left-0 right-0 p-4">
          <h4 className="font-display text-xl text-white leading-tight">{item.name}</h4>
          <p className="text-[11px] tracking-wide text-zinc-300 mt-0.5">{item.desc}</p>
        </div>
      </div>
      <div className="p-4 flex items-center justify-between gap-3 border-t border-[color:var(--gold)]/20 bg-black/40">
        <div>
          <p className="text-[9px] tracking-[0.25em] uppercase text-zinc-500">
            {t.services.fromLabel}
          </p>
          <p className="text-lg font-display gold-text leading-none mt-1">{item.price}</p>
        </div>
        <span className="inline-flex items-center gap-1.5 bg-[color:var(--gold)] text-black text-xs font-semibold tracking-wide px-3 py-2 transition-transform group-hover:translate-x-0.5">
          {t.services.reserveCta}
          <ArrowRight className="h-3.5 w-3.5" />
        </span>
      </div>
    </a>
  );
};

const LeisureCard = ({ item, t }) => {
  const Icon = ICONS[item.id] || Castle;
  const onClick = (e) => {
    e.preventDefault();
    triggerBooking(item.name);
  };
  return (
    <a
      key={item.id}
      href="#booking"
      onClick={onClick}
      data-testid={`service-card-${item.id}`}
      className="bento-card group block"
    >
      <div className="relative h-56 sm:h-60 overflow-hidden">
        <img
          src={IMAGES[item.id]}
          alt={item.name}
          className="bento-image w-full h-full object-cover"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/55 to-black/10" />
        <div className="absolute top-4 left-4 flex items-center gap-2 px-2.5 py-1 bg-black/60 border border-white/10 backdrop-blur-sm">
          <Icon className="h-3.5 w-3.5 text-[color:var(--gold)]" />
          <span className="text-[10px] tracking-[0.25em] uppercase text-zinc-200">
            Destino
          </span>
        </div>
        <div className="absolute bottom-0 left-0 right-0 p-5">
          <h3 className="font-display text-2xl text-white">{item.name}</h3>
          <p className="text-sm text-zinc-300 mt-1">{item.desc}</p>
        </div>
      </div>
      <div className="p-5 flex items-center justify-between border-t border-white/5">
        <div>
          <p className="text-[10px] tracking-[0.25em] uppercase text-zinc-500">
            {t.services.fromLabel}
          </p>
          <p className="text-xl font-display gold-text mt-0.5">{item.price}</p>
        </div>
        <span className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-zinc-400 group-hover:text-[color:var(--gold)] transition-colors">
          {t.services.cta}
          <ArrowUpRight className="h-4 w-4" />
        </span>
      </div>
    </a>
  );
};

const Services = () => {
  const { t } = useLang();
  const airports = t.services.items.filter((it) => AIRPORT_IDS.includes(it.id));
  const leisure = t.services.items.filter((it) => !AIRPORT_IDS.includes(it.id));

  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative py-24 sm:py-32 bg-[color:var(--bg-primary)]"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="max-w-2xl">
          <p className="label-eyebrow">{t.services.eyebrow}</p>
          <h2
            data-testid="services-title"
            className="font-display text-3xl sm:text-4xl lg:text-5xl mt-4 text-white tracking-tight"
          >
            {t.services.title}
          </h2>
          <p className="mt-5 text-zinc-400 text-base sm:text-lg leading-relaxed">
            {t.services.subtitle}
          </p>
        </div>

        {/* AIRPORTS — featured */}
        <div className="mt-14" data-testid="airports-group">
          <div className="airport-banner mb-6">
            <div className="flex items-start sm:items-center justify-between gap-4 flex-col sm:flex-row">
              <div className="flex items-center gap-3">
                <span className="airport-banner-icon">
                  <Plane className="h-5 w-5" />
                </span>
                <div>
                  <p className="text-[10px] tracking-[0.3em] uppercase text-[color:var(--gold)]">
                    {t.services.airportsLabel}
                  </p>
                  <p className="text-sm text-zinc-300 mt-1">{t.services.airportsHint}</p>
                </div>
              </div>
              <a
                href="#booking"
                onClick={(e) => {
                  e.preventDefault();
                  triggerBooking("Aeroporto de Faro");
                }}
                data-testid="airports-book-cta"
                className="btn-gold !py-2.5 !px-5 text-sm"
              >
                {t.services.reserveCta}
                <ArrowRight className="h-4 w-4" />
              </a>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
            {airports.map((item) => (
              <AirportCard key={item.id} item={item} t={t} />
            ))}
          </div>
        </div>

        {/* LEISURE */}
        <div className="mt-16">
          <div className="flex items-center gap-3 mb-6">
            <span className="gold-divider !w-8 !mx-0" />
            <p className="text-[10px] tracking-[0.3em] uppercase text-zinc-400">
              {t.services.leisureLabel}
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {leisure.map((item) => (
              <LeisureCard key={item.id} item={item} t={t} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Services;
